<template>
  <div>
    <h1 class="mb-8 font-bold text-3xl">Kunden</h1>
    <div class="mb-6 flex justify-between items-center">
      <search-filter v-model="form.search" class="w-full max-w-md mr-4" @reset="reset">
        <label class="block text-gray-700">Status:</label>
        <select v-model="form.status" class="mt-1 w-full form-select">
          <option :value="null" />
          <option value="not_processed">{{ statuses.not_processed }}</option>
          <option value="in_process">{{ statuses.in_process }}</option>
          <option value="proposal_created">{{ statuses.finished_positive }}</option>
          <option value="missing_documents">{{ statuses.finished_negative }}</option>
        </select>
        <label class="mt-4 block text-gray-700">Trashed:</label>
        <select v-model="form.trashed" class="mt-1 w-full form-select">
          <option :value="null" />
          <option value="with">Mit Trashed</option>
          <option value="only">Nur Trashed</option>
        </select>
        <label class="mt-4 block text-gray-700">Benutzer:</label>
        <select v-model="form.user_id" class="mt-1 w-full form-select">
          <option :value="null" />
          <option
            v-for="user in users" :key="user.id"
            :value="user.id"
          >
            {{ user.first_name + ' ' + user.last_name }}
          </option>
        </select>
        <label class="mt-4 block text-gray-700">Kredit Typ:</label>
        <select v-model="form.type" class="mt-1 w-full form-select">
          <option :value="null" />
          <option value="private_credit">Privatkredit</option>
          <option value="property_credit">Immobilienkredit</option>
        </select>
      </search-filter>
      <div class="w-full flex justify-end space-x-2">
        <a
          download
          class="btn-green mr-1"
          :href="route('clients.downloadCSV')
            + `?search=${form.search ? form.search : ''}&status=${form.status ? form.status : ''}&trashed=${form.trashed ? form.trashed : ''}&user_id=${form.user_id ? form.user_id : ''}`"
          onclick="alert('CSV-Datei wird erstellt. Bitte schließen Sie diese Seite nicht und warten Sie einige Sekunden.')"
        >
          ^
          <span class="hidden md:inline">Export</span>
          <span class="hidden md:inline">Kunden</span>
        </a>
        <inertia-link class="btn-green mt-sm-4 d-block" :href="route('clients.create')">
          +
          <span class="hidden md:inline">Erschaffen</span>
        </inertia-link>
      </div>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="px-6 pt-6 pb-4">Hinzicht</th>
          <th class="px-6 pt-6 pb-4">
            Name
            <a :href="currentRoute + querySign + 'orderBy=first_name-asc'">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" class="iconify iconify--mdi float-right" width="24" height="24" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24"><path d="M7.03 9.97h4v8.92l2.01.03V9.97h3.99l-5-5z" fill="currentColor" /></svg>
            </a>
            <a :href="currentRoute + querySign + 'orderBy=first_name-desc'">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" class="iconify iconify--mdi float-right" width="24" height="24" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24"><path d="M7.03 13.92h4V5l2.01-.03v8.95h3.99l-5 5z" fill="currentColor" /></svg>
            </a>
          </th>
          <th class="px-6 pt-6 pb-4">Telefon</th>
          <th class="px-6 pt-6 pb-4">
            Status
            <a :href="currentRoute + querySign + 'orderBy=status-asc'">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" class="iconify iconify--mdi float-right" width="24" height="24" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24"><path d="M7.03 9.97h4v8.92l2.01.03V9.97h3.99l-5-5z" fill="currentColor" /></svg>
            </a>
            <a :href="currentRoute + querySign + 'orderBy=status-desc'">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" class="iconify iconify--mdi float-right" width="24" height="24" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24"><path d="M7.03 13.92h4V5l2.01-.03v8.95h3.99l-5 5z" fill="currentColor" /></svg>
            </a>
          </th>
          <th class="px-6 pt-6 pb-4">Kredit Typ</th>
          <th class="px-6 pt-6 pb-4" colspan="2">
            Zugewiesen an
          </th>
        </tr>
        <tr v-for="client in clients.data" :key="client.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
          <td class="border-t" style="width: 100px;">
            <button
              v-if="client.has_employee_notification"
              title="Mitarbeiter Benachrichtigung"
              class="text-gray-400 hover:text-gray-600 focus:outline-none btn-orange-small ml-1 my-1"
              style="width: 40px; height: 40px; border-radius: 100%"
              @click="updateNotificationIcon('has_employee_notification', client.id)"
            >
              <icon name="notification" style="width: 24px; height: 24px; margin-left: -8px" />
            </button>
            <button
              v-if="client.has_advisor_notification"
              title="Berater Benachrichtigung"
              class="text-gray-400 hover:text-gray-600 focus:outline-none btn-orange-small ml-1 my-1"
              style="width: 40px; height: 40px; border-radius: 100%"
              @click="updateNotificationIcon('has_advisor_notification', client.id)"
            >
              <icon name="alarm" style="width: 24px; height: 24px; margin-left: -5px; margin-top:3px" />
            </button>
            <button
              v-if="client.has_quality_notification"
              title="Quality Benachrichtigung"
              class="text-gray-400 hover:text-gray-600 focus:outline-none btn-orange-small ml-1 my-1"
              style="width: 40px; height: 40px; border-radius: 100%"
              @click="updateNotificationIcon('has_quality_notification', client.id)"
            >
              <icon name="quality" style="width: 24px; height: 24px; margin-left: -6px; margin-top: 2px" />
            </button>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-kreditgenie-text-green" :href="route('clients.edit', client.id)">
              {{ client.name }}
              <icon v-if="client.deleted_at" name="trash" class="flex-shrink-0 w-3 h-3 fill-gray-400 ml-2" />
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center" :href="route('clients.edit', client.id)" tabindex="-1">
              {{ client.phone }}
            </inertia-link>
          </td>
          <td class="border-t items-end">
            <div class="flex w-full">
              <inertia-link
                class="px-6 py-4 flex items-center text-white"
                :class="`status-${client.status}`"
                :href="route('clients.edit', client.id)" tabindex="-1"
                style="width: 166px"
              >
                {{ statuses[client.status] }}
              </inertia-link>
              <button
                class="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none text-right btn-green-small py-1 px-1 mt-1"
                style="width: 40px; height: 40px; border-radius: 100%"
                @click="() => {selectedClient = client; getClientNotes(client.id); qualityCheckDateFormatted = formatDateToDDMMYYYY(client.quality_check_date); }"
              >
                <icon name="edit" style="width: 20px; height: 20px; margin-left: -5px" />
              </button>
              <inertia-link
                class="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none text-right btn-green-small py-1 px-1 mt-1"
                style="width: 40px; height: 40px; border-radius: 100%"
                :href="route('client.documents', client.id)"
              >
                <icon name="documents" style="width: 20px; height: 20px; margin-left: -5px; color: white" fill="#FFFFFF" />
              </inertia-link>
            </div>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center" :href="route('clients.edit', client.id)" tabindex="-1">
              {{ client.type === 'property_credit' ? 'Immobilienkredit' : 'Privatkredit' }}
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center text-center" :href="route('clients.edit', client.id)" tabindex="-1">
              {{ getUserData(client.user_id) }}
            </inertia-link>
          </td>
          <td class="border-t w-px">
            <inertia-link class="px-4 flex items-center" :href="route('clients.edit', client.id)" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </inertia-link>
          </td>
        </tr>
        <tr v-if="clients.length === 0">
          <td class="border-t px-6 py-4" colspan="4">Keine Kunden gefunden.</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="clients.links" />
    <modal
      :show="!!selectedClient"
      @close="selectedClient = null"
    >
      <div class="overflow-y-auto" style="max-height: 80vh">
        <h1 class="text-lg font-bold uppercase border-b">Statusverlauf</h1>
        <div v-if="selectedClient">
          <p class="py-2"><strong>Name:</strong> {{ selectedClient.name || `${selectedClient.first_name} ${selectedClient.last_name}` }}</p>
          <p class="py-2"><strong>Email:</strong> {{ selectedClient.email }}</p>
          <p class="py-2"><strong>Telefon:</strong> {{ selectedClient.phone }}</p>
          <p class="py-2"><strong>Gewünschter Kreditbetrag:</strong> {{ selectedClient.wanted_credit_amount || selectedClient.credit_amount }}</p>
          <p class="py-2"><strong>Statusverlauf:</strong></p>
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <td class="py-4 px-4 border-r border-white">Status</td>
                <td class="py-4 px-4 border-r border-white">Zustand</td>
                <td class="py-4 px-4 border-r border-white">Zeitpunkt</td>
                <td class="py-4 px-4">Benutzer</td>
              </tr>
            </thead>
            <tr
              v-for="(status) in statusFlags()"
              :key="status.key"
            >
              <td
                class="border-b py-2 px-4"
                :class="{
                  'bg-yellow-200': ((selectedClient.client_status || []).find(item => item.name === status.key) || {}).state === 'in_progress',
                  'bg-green-200': ((selectedClient.client_status || []).find(item => item.name === status.key) || {}).state === 'completed',
                }"
              >
                {{ status.value }}
              </td>
              <td>
                <select-input
                  v-model="((selectedClient.client_status || []).find(item => item.name === status.key) || {}).state"
                  class="px-2"
                  @input="statusFlagChange(selectedClient.id, status.key, $event)"
                >
                  <option v-for="item in [
                            {key: '', name: ''},
                            {key: 'in_progress', name: 'In Bearbeitung'},
                            {key: 'completed', name: 'Abgeschlossen'},
                          ]"
                          :key="item.key"
                          :value="item.key"
                  >
                    {{ item.name }}
                  </option>
                </select-input>
              </td>
              <td>
                {{ getChangeTime(selectedClient, status) }}
              </td>
              <td>
                {{ getChangeUser(selectedClient, status, users) }}
              </td>
            </tr>
            <tr><td colspan="4">&nbsp;</td></tr>
            <tr class="py-5">
              <td
                class="border-b py-2 px-4"
                :class="`status-${selectedClient.status}`"
              >
                <strong>General Status</strong>
              </td>
              <td style="width: 156px">
                <select-input
                  v-model="selectedClient.status"
                  class="w-full px-2"
                  @input="statusGeneralChange(selectedClient.id, $event)"
                >
                  <option
                    v-for="(status) in convertStatusesToArray(statuses)"
                    :key="status.key"
                    :value="status.key"
                  >
                    {{ statuses[status.key] }}
                  </option>
                </select-input>
              </td>
              <td>
                {{ selectedClient.status_changed_at ? selectedClient.status_changed_at : '' }}
              </td>
              <td>
                {{ selectedClient.user_id ? getUserData(selectedClient.user_id) : '' }}
              </td>
            </tr>
            <tr><td colspan="4">&nbsp;</td></tr>
            <tr class="py-5">
              <td class="py-2" colspan="5">
                <div class="w-full my-b mt-8">
                  <h1 class="text-lg font-bold uppercase border-b">Wirtschaftlicher Übergang</h1>
                </div>
              </td>
            </tr>
            <tr>
              <td colspan="2">
                <p class="text-xs text-gray-500 mt-1">Quality-Benachrichtigung wird 2 Wochen vorher aktiviert (Format: TT/MM/JJJJ)</p>
                <input
                  v-model="qualityCheckDateFormatted"
                  type="text"
                  placeholder="TT/MM/JJJJ"
                  maxlength="10"
                  class="w-full px-2 py-1 border rounded"
                  @input="onQualityCheckDateInput"
                />
              </td>
              <td>
                <span v-if="selectedClient.quality_check_date" class="text-green-600 text-sm mt-1">
                  ✓ Aktiviert
                </span>
                <span v-else class="text-xs text-gray-500 mt-1">
                  ✗ Deaktiviert
                </span>
                <button
                  class="text-gray-400 hover:text-gray-600 focus:outline-none btn-green-small w-full sm:w-auto mb-2 sm:my-5 sm:ml-1 flex items-center justify-center"
                  @click="saveQualityCheckDate"
                >
                  Speichern
                </button>
              </td>
            </tr>
          </table>
          <div class="w-full my-b mt-8">
            <h1 class="text-lg font-bold uppercase border-b">Notizen</h1>
          </div>
          <div class="flex my-4">
            <div class="w-full">
              <textarea-input
                v-model="noteText"
                class="w-full"
                label="Notiz"
              />
              <div class="w-full text-center">
                <button
                  class="btn-green mt-2"
                  @click="addNote(selectedClient.id, noteText)"
                >
                  <span>Notiz speichern</span>
                </button>
              </div>
            </div>
          </div>
          <div class="w-full mt-2">
            <div v-for="note in selectedClientNotes" :key="note.id" class="w-full mt-1">
              <div class="bg-gray-100 p-2 rounded-md mb-1">
                <!-- delete-note -->
                <div
                  class="float-right text-red-500 cursor-pointer border-red-500 border rounded-full w-6 h-6 flex items-center justify-center"
                  @click="deleteNote(note.id, selectedClient.id)"
                >
                  <icon name="trash" class="w-3 h-3 fill-red-500" />
                </div>
                <p class="text-sm" v-html="note.content" />
                <p class="text-xs text-gray-500 mt-1">
                  <strong>{{ getUserData(note.user_id) }}</strong> - {{ note.created_at }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="w-full my-b mt-8">
          <h1 class="text-lg font-bold uppercase border-b">Benachrichtigungen für diesen Client anzeigen:</h1>
        </div>
        <div class="w-full text-center">
          <div class="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-2 sm:gap-1 px-2 sm:px-0">
            <button
              class="text-gray-400 hover:text-gray-600 focus:outline-none btn-green-small w-full sm:w-auto my-2 sm:my-5 sm:ml-1 flex items-center justify-center"
              :class="selectedClientEmployeeNotification"
              @click="updateNotificationIcon('has_employee_notification', selectedClient.id)"
            >
              <icon name="notification" class="flex-shrink-0" style="width: 25px; height: 25px; margin-right: 8px;" />
              <span class="whitespace-nowrap">Servicemitarbeiter</span>
            </button>
            <button
              class="text-gray-400 hover:text-gray-600 focus:outline-none btn-green-small w-full sm:w-auto my-2 sm:my-5 sm:ml-1 flex items-center justify-center"
              :class="selectedClientAdvisorNotification"
              @click="updateNotificationIcon('has_advisor_notification', selectedClient.id)"
            >
              <icon name="alarm" class="flex-shrink-0" style="width: 22px; height: 22px; margin-right: 8px;" />
              <span class="whitespace-nowrap">Berater</span>
            </button>
            <button
              class="text-gray-400 hover:text-gray-600 focus:outline-none btn-green-small w-full sm:w-auto my-2 sm:my-5 sm:ml-1 flex items-center justify-center"
              :class="selectedQualityAssuranceNotification"
              @click="updateNotificationIcon('has_quality_notification', selectedClient.id)"
            >
              <icon name="quality" class="flex-shrink-0" style="width: 22px; height: 22px; margin-right: 8px;" />
              <span class="whitespace-nowrap">Quality</span>
            </button>
            <button
              class="text-gray-400 hover:text-gray-600 focus:outline-none btn-green-small w-full sm:w-auto my-2 sm:my-5 sm:ml-1 flex items-center justify-center"
              :class="isPushedToTop"
              @click="updateNotificationIcon('force_sort_top', selectedClient.id)"
            >
              <icon name="go-to-top" class="flex-shrink-0" style="width: 20px; height: 20px; margin-right: 8px;" />
              <span class="whitespace-nowrap">Nach oben</span>
            </button>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '../../Shared/Pagination'
import statuses from './statuses'
import Modal from '@/Shared/Modal.vue'
import {statusFlags} from './statusFlags'
import SelectInput from '../../Shared/SelectInput.vue'
import {Inertia} from '@inertiajs/inertia'
import {convertStatusesToArray, getLastStatus} from './statesHelper'
import {getChangeTime, getChangeUser} from '../../utils'
import TextareaInput from '../../Shared/TextareaInput.vue'

export default {
  metaInfo: { title: 'Clients' },
  components: {
    TextareaInput,
    SelectInput,
    Modal,
    Pagination,
    Icon,
    SearchFilter,
  },
  layout: Layout,
  props: {
    filters: Object,
    clients: Object,
    users: Array,
  },
  data() {
    const currentRoute = location.href.split('?')[0]
    const querySign = currentRoute.indexOf('?') !== -1 ? '&' : '?'
    return {
      form: {
        search: this.filters.search,
        status: this.filters.status,
        trashed: this.filters.trashed,
        user_id: null,
        type: this.filters.type,
      },
      currentRoute,
      querySign,
      statuses,
      selectedClient: null,
      noteText: '',
      selectedClientNotes: [],
      qualityCheckDateFormatted: '',
    }
  },
  computed: {
    selectedClientEmployeeNotification() {
      return this.selectedClient && this.selectedClient.has_employee_notification
        ? 'btn-orange-small'
        : 'btn-green-small'
    },
    selectedClientAdvisorNotification() {
      return this.selectedClient && this.selectedClient.has_advisor_notification
        ? 'btn-orange-small'
        : 'btn-green-small'
    },
    selectedQualityAssuranceNotification() {
      return this.selectedClient && this.selectedClient.has_quality_notification
        ? 'btn-orange-small'
        : 'btn-green-small'
    },
    isPushedToTop() {
      return this.selectedClient && this.selectedClient.is_pushed_to_top
        ? 'btn-orange-small'
        : 'btn-green-small'
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(this.route('clients'), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    getChangeUser,
    getChangeTime,
    convertStatusesToArray,
    getLastStatus,
    updateNotificationIcon(name, clientId) {
      fetch(`/api/client-notification-icon/${clientId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId,
          userId: this.$page.props.auth.user.id,
          notificationIconType: name,
        }),
      })
        .then(res => res.json())
        .then((data) => {
          if(this.selectedClient) {
            this.selectedClient.has_employee_notification = data.client.has_employee_notification
            this.selectedClient.has_advisor_notification = data.client.has_advisor_notification
            this.selectedClient.has_quality_notification = data.client.has_quality_notification
            this.selectedClient.is_pushed_to_top = data.client.is_pushed_to_top
          }
          Inertia.reload()
        })
        .catch(error => {
          console.error('Error:', error)
        })
    },
    deleteNote(id, clientId) {
      if (!confirm('Sind Sie sicher, dass Sie diese Notiz löschen möchten?')) {
        return
      }
      fetch(`/api/client-notes/${id}/${clientId}`, {
        method: 'DELETE',
      })
        .then(res => res.json())
        .then(data => {
          Inertia.reload()
          this.selectedClientNotes = data
        })
        .catch(error => {
          console.error('Error:', error)
        })
    },
    addNote(selectedClientId, noteText) {
      if (!noteText) {
        alert('Bitte geben Sie eine Notiz ein.')
        return
      }
      fetch('/api/client-notes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId: selectedClientId,
          content: noteText,
          userId: this.$page.props.auth.user.id,
        }),
      })
        .then(res => res.json())
        .then(data => {
          Inertia.reload()
          this.selectedClientNotes = data
          this.noteText = ''
        })
        .catch(error => {
          console.error('Error:', error)
        })
    },
    async getClientNotes(clientId) {
      await fetch(`/api/client-notes/${clientId}`)
        .then(res => res.json())
        .then(data => {
          this.selectedClientNotes = data
          return data
        })
        .catch(error => {
          console.error('Error:', error)
          return []
        })
    },
    statusFlagChange(clientId, name, state) {
      fetch('/api/client-status', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, state, clientId, userId: this.$page.props.auth.user.id }),
      })
        .then(res => res.json())
        .then(data => {
          Inertia.reload()
          this.selectedClient = {...data}
        })
        .catch(error => {
          console.error('Error:', error)
        })
    },
    statusGeneralChange(clientId, status) {
      fetch('/api/client-status/general', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, clientId, userId: this.$page.props.auth.user.id }),
      })
        .then(res => res.json())
        .then(data => {
          Inertia.reload()
          this.selectedClient = {...data}
        })
        .catch(error => {
          console.error('Error:', error)
        })
    },
    updateQualityCheckDate(clientId, date) {
      fetch('/api/client-quality-check-date', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quality_check_date: date, clientId, userId: this.$page.props.auth.user.id }),
      })
        .then(res => res.json())
        .then(data => {
          if (data.updated === false) {
            alert(data.message)
          } else {
            Inertia.reload()
            this.selectedClient = {...data}
          }
        })
        .catch(error => {
          console.error('Error:', error)
          alert('Fehler beim Aktualisieren des Quality Check Datums')
        })
    },
    statusFlags() {
      return statusFlags
    },
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    getUserData(id) {
      const user = this.users.find((user) => user.id === id)
      if(user) {
        return user.first_name + ' ' + user.last_name
      }
      return 'Nicht zugeordnet'
    },
    formatDateToDDMMYYYY(dateString) {
      if (!dateString) return ''
      // Convert yyyy-mm-dd to dd/mm/yyyy
      const parts = dateString.split('-')
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      return dateString
    },
    formatDateToYYYYMMDD(dateString) {
      if (!dateString) return null
      // Convert dd/mm/yyyy to yyyy-mm-dd
      const parts = dateString.split('/')
      if (parts.length === 3) {
        const day = parts[0].padStart(2, '0')
        const month = parts[1].padStart(2, '0')
        const year = parts[2]
        return `${year}-${month}-${day}`
      }
      return null
    },
    onQualityCheckDateInput(event) {
      let value = event.target.value

      // Remove any non-digit and non-slash characters
      value = value.replace(/[^\d/]/g, '')

      // Get only the digits to rebuild the format
      const digitsOnly = value.replace(/\//g, '')

      // Allow completely empty input
      if (digitsOnly.length === 0) {
        this.qualityCheckDateFormatted = ''
        return
      }

      // Build formatted string with slashes
      let formatted = ''

      if (digitsOnly.length > 0) {
        // Day (max 2 digits)
        formatted = digitsOnly.slice(0, 2)
      }

      if (digitsOnly.length >= 3) {
        // Add slash and month (max 2 digits)
        formatted += '/' + digitsOnly.slice(2, 4)
      }

      if (digitsOnly.length >= 5) {
        // Add slash and year (max 4 digits)
        formatted += '/' + digitsOnly.slice(4, 8)
      }

      this.qualityCheckDateFormatted = formatted
    },
    saveQualityCheckDate() {
      // Allow clearing the date
      if (this.qualityCheckDateFormatted === '' || !this.qualityCheckDateFormatted) {
        this.updateQualityCheckDate(this.selectedClient.id, null)
        return
      }

      // Check if the date is complete (dd/mm/yyyy = 10 characters)
      if (this.qualityCheckDateFormatted.length !== 10) {
        alert('Bitte geben Sie ein vollständiges Datum ein (TT/MM/JJJJ)')
        return
      }

      const isoDate = this.formatDateToYYYYMMDD(this.qualityCheckDateFormatted)

      if (!isoDate) {
        alert('Ungültiges Datumsformat. Bitte verwenden Sie TT/MM/JJJJ')
        return
      }

      // Validate the date is valid (e.g., not 32/13/2025)
      const parts = this.qualityCheckDateFormatted.split('/')
      const day = parseInt(parts[0])
      const month = parseInt(parts[1])
      const year = parseInt(parts[2])

      if (day < 1 || day > 31 || month < 1 || month > 12 || year < 2000) {
        alert('Ungültiges Datum. Bitte überprüfen Sie Tag, Monat und Jahr.')
        return
      }

      // Validate the date is in the future
      const selectedDate = new Date(isoDate)
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      if (isNaN(selectedDate.getTime())) {
        alert('Ungültiges Datum.')
        return
      }

      if (selectedDate <= today) {
        alert('Das Datum muss in der Zukunft liegen.')
        return
      }

      this.updateQualityCheckDate(this.selectedClient.id, isoDate)
    },
  },
}
</script>
